'use client'

import { useCallback, useEffect, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'

import { useCreatePromotion, useUpdatePromotion } from '@/lib/api/promotions'
import { PromotionActionsSection } from '@/components/form/discount/promotion-actions-section'
import type { Action, PromotionFormData } from '@/components/form/discount/promotion-form'

type ActionType =
    | 'LineItemAdjustment'
    | 'OrderAdjustment'
    | 'ShippingAdjustment'
    | 'CreateLineItem'
    | 'SubscriptionLineItemFreeTrial'
    | 'BOGO'

export interface VipDiscountPromotion {
    id: string
    name: string
    actions: any[]
}

interface VipDiscountDialogProps {
    storeId: string
    /** The club's VIP membership product — used for the contains-product rule. */
    productId: string
    /** When set, the dialog edits this discount instead of creating one. */
    promotion?: VipDiscountPromotion | null
    open: boolean
    onOpenChange: (open: boolean) => void
    onSaved?: () => void
}

const DEFAULT_FORM: PromotionFormData = {
    name: '',
    enabled: true,
    automatic: true,
    usageLimit: undefined,
    startDate: new Date(),
    endDate: null,
    ruleOperator: 'OR',
    combinesWithOrderLevelDiscounts: true,
    combinesWithLineItemDiscounts: true,
    combinesWithShippingDiscounts: true,
    forceCombine: true,
    oncePerCustomer: false,
    rules: [],
    actions: [],
}

// Map a stored action row into the editable form Action shape.
const toFormAction = (a: any): Action => ({
    type: a.type,
    adjustmentType: a.adjustmentType ?? undefined,
    adjustmentPercentage: a.adjustmentPercentage ?? undefined,
    adjustmentAmount: a.adjustmentAmount ?? undefined,
    freeShipping: a.freeShipping ?? undefined,
    productIdToAdd: a.productIdToAdd ?? undefined,
    targetProductId: a.targetProductId ?? undefined,
    targetVariantIds: a.targetVariantIds ?? undefined,
    maxQuantityDiscounted: a.maxQuantityDiscounted ?? undefined,
    appliesOnEachItem: a.appliesOnEachItem ?? undefined,
    subscriptionFreeTrialDuration: a.subscriptionFreeTrialDuration ?? undefined,
    subscriptionFreeTrialDurationType: a.subscriptionFreeTrialDurationType ?? undefined,
    priceIdToAdd: a.priceIdToAdd ?? undefined,
    variantIdToAdd: a.variantIdToAdd ?? undefined,
    quantityToAdd: a.quantityToAdd ?? undefined,
    buyQuantityThreshold: a.buyQuantityThreshold ?? undefined,
})

/**
 * Minimal VIP discount creator/editor. The merchant sets a name and the
 * discount actions (full multi-action editor). Everything that makes a
 * promotion "VIP" is prefilled: enabled, automatic, force-combine + all
 * combinations, no usage limit, not once-per-customer, and a ContainsProduct
 * rule targeting the club's VIP product.
 */
export const VipDiscountDialog = ({
    storeId,
    productId,
    promotion,
    open,
    onOpenChange,
    onSaved,
}: VipDiscountDialogProps) => {
    const { t } = useTranslation('club')
    const { toast } = useToast()
    const isEdit = !!promotion?.id

    const createPromotion = useCreatePromotion()
    const updatePromotion = useUpdatePromotion()

    const form = useForm<PromotionFormData>({ defaultValues: DEFAULT_FORM })
    const { watch, setValue, getValues, reset } = form
    const name = watch('name')
    const actions = watch('actions')

    // Populate when editing (once per opened promotion).
    const loadedRef = useRef<string | null>(null)
    useEffect(() => {
        if (!open) {
            loadedRef.current = null
            return
        }
        if (isEdit && promotion && loadedRef.current !== promotion.id) {
            loadedRef.current = promotion.id
            reset({
                ...DEFAULT_FORM,
                name: promotion.name,
                actions: (promotion.actions || []).map(toFormAction),
            })
        }
    }, [open, isEdit, promotion, reset])

    const addAction = useCallback(
        (type: ActionType) => {
            const newAction: Action = { type }
            if (type === 'LineItemAdjustment' || type === 'OrderAdjustment') {
                newAction.adjustmentType = 'percentage'
                newAction.adjustmentPercentage = 10
            } else if (type === 'ShippingAdjustment') {
                newAction.freeShipping = true
            } else if (type === 'SubscriptionLineItemFreeTrial') {
                newAction.subscriptionFreeTrialDuration = 7
                newAction.subscriptionFreeTrialDurationType = 'day'
            } else if (type === 'BOGO') {
                newAction.buyQuantityThreshold = 1
                newAction.quantityToAdd = 1
            }
            setValue('actions', [...getValues('actions'), newAction])
        },
        [setValue, getValues],
    )

    const updateAction = useCallback(
        (index: number, updates: Partial<Action>) => {
            const next = [...getValues('actions')]
            next[index] = { ...next[index], ...updates }
            setValue('actions', next)
        },
        [setValue, getValues],
    )

    const removeAction = useCallback(
        (index: number) => {
            setValue(
                'actions',
                getValues('actions').filter((_, i) => i !== index),
            )
        },
        [setValue, getValues],
    )

    const handleOpenChange = (next: boolean) => {
        if (!next) {
            reset(DEFAULT_FORM)
            loadedRef.current = null
        }
        onOpenChange(next)
    }

    const canSubmit = name?.trim().length > 0 && actions.length > 0 && !!productId
    const isBusy = createPromotion.isPending || updatePromotion.isPending

    const handleSubmit = async () => {
        try {
            const data = getValues()
            const payload = {
                storeId,
                name: data.name.trim(),
                // --- prefilled "VIP" configuration ---
                enabled: true,
                automatic: true,
                usageLimit: null,
                oncePerCustomer: false,
                forceCombine: true,
                combinesWithOrderLevelDiscounts: true,
                combinesWithLineItemDiscounts: true,
                combinesWithShippingDiscounts: true,
                ruleOperator: 'OR' as const,
                rules: [{ type: 'ContainsProduct', productId }],
                actions: data.actions.map((action) => ({
                    type: action.type,
                    adjustmentType: action.adjustmentType,
                    adjustmentPercentage: action.adjustmentPercentage,
                    adjustmentAmount: action.adjustmentAmount,
                    freeShipping: action.freeShipping,
                    productIdToAdd: action.productIdToAdd,
                    targetProductId: action.targetProductId,
                    targetVariantIds: action.targetVariantIds,
                    maxQuantityDiscounted: action.maxQuantityDiscounted,
                    appliesOnEachItem: action.appliesOnEachItem,
                    subscriptionFreeTrialDuration: action.subscriptionFreeTrialDuration,
                    subscriptionFreeTrialDurationType: action.subscriptionFreeTrialDurationType,
                    priceIdToAdd: action.priceIdToAdd,
                    variantIdToAdd: action.variantIdToAdd,
                    quantityToAdd: action.quantityToAdd,
                    buyQuantityThreshold: action.buyQuantityThreshold,
                })),
            }

            if (isEdit && promotion) {
                await updatePromotion.mutateAsync({ id: promotion.id, data: payload } as any)
            } else {
                await createPromotion.mutateAsync(payload as any)
            }

            toast({
                title: isEdit ? t('vipDiscount.toasts.updatedTitle') : t('vipDiscount.toasts.createdTitle'),
                description: isEdit ? t('vipDiscount.toasts.updatedDesc') : t('vipDiscount.toasts.createdDesc'),
            })
            reset(DEFAULT_FORM)
            loadedRef.current = null
            onOpenChange(false)
            onSaved?.()
        } catch {
            // create/update hooks surface an error toast
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="w-full sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{isEdit ? t('vipDiscount.editTitle') : t('vipDiscount.title')}</DialogTitle>
                    <DialogDescription>{t('vipDiscount.description')}</DialogDescription>
                </DialogHeader>

                <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
                    <div className="space-y-1">
                        <Label htmlFor="vip-discount-name">{t('vipDiscount.name')}</Label>
                        <Input
                            id="vip-discount-name"
                            value={name}
                            onChange={(e) => setValue('name', e.target.value)}
                            placeholder={t('vipDiscount.namePlaceholder')}
                        />
                    </div>

                    <FormProvider {...form}>
                        <PromotionActionsSection
                            storeId={storeId}
                            onAddAction={addAction}
                            onUpdateAction={updateAction}
                            onRemoveAction={removeAction}
                            hideAdvancedTargeting
                        />
                    </FormProvider>

                    <p className="text-muted-foreground text-xs">{t('vipDiscount.prefillNote')}</p>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isBusy}>
                        {t('vipDiscount.cancel')}
                    </Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit || isBusy}>
                        {isBusy
                            ? isEdit
                                ? t('vipDiscount.saving')
                                : t('vipDiscount.creating')
                            : isEdit
                              ? t('vipDiscount.saveChanges')
                              : t('vipDiscount.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
